import { useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { CornerDownLeftIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ClientsPage } from "./pages/ClientsPage";
import { ClientPage } from "./pages/ClientPage";
import { ClientOrganisationsPage } from "./pages/OrganisationsPage";
import { OrganisationsListPage } from "./pages/OrganisationsListPage";
import { OrganisationPage } from "./pages/OrganisationPage";
import { CompanyPage } from "./pages/CompanyPage";
import { CompanyInboxBillPage } from "./pages/CompanyInboxBillPage";
import { MatchGroupsPage } from "./pages/MatchGroupsPage";
import { MatchGroupPage } from "./pages/MatchGroupPage";
import { InvoicePage } from "./pages/InvoicePage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { BillsPage } from "./pages/BillsPage";
import { ExpensesPage } from "./pages/ExpensesPage";
import { ExpensePage } from "./pages/ExpensePage";
import { CustomerPage } from "./pages/CustomerPage";
import { ProductPage } from "./pages/ProductPage";
import { PurchaseOrderPage } from "@/pages/PurchaseOrderPage";
import { DeliveryNotePage } from "@/pages/DeliveryNotePage";
import { BillPage } from "./pages/BillPage";
import { SupplierPage } from "./pages/SupplierPage";
import { UserPage } from "./pages/UserPage";
import { UsersPage } from "./pages/UsersPage";
import { DemosPage } from "./pages/DemosPage";
import { CountriesPage } from "./pages/CountriesPage";
import { CurrenciesPage } from "./pages/CurrenciesPage";
import { IndustriesPage } from "./pages/IndustriesPage";
import { CountryTaxRatesPage } from "./pages/CountryTaxRatesPage";
import { AgentConversationPage } from "./pages/AgentConversationPage";
import { InvoiceRemindersPage } from "./pages/InvoiceRemindersPage";
import { WebhookDeliveriesPage } from "./pages/WebhookDeliveriesPage";
import { ReceivedEInvoicesPage } from "@/pages/ReceivedEInvoicesPage";
import { SentEInvoicesPage } from "@/pages/SentEInvoicesPage";
import { EInvoicingRegistrationsPage } from "@/pages/EInvoicingRegistrationsPage";
import { GmailConnectionsPage } from "./pages/GmailConnectionsPage";
import { GmailConnectionPage } from "./pages/GmailConnectionPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { ThreeWayMatchPage } from "./pages/ThreeWayMatchPage";
import { UserActivityPage } from "./pages/UserActivityPage";
import { InvoiceAuditLogsPage } from "./pages/InvoiceAuditLogsPage";
import { BillAuditLogsPage } from "./pages/BillAuditLogsPage";
import { ModuleAuditLogsPage } from "./pages/ModuleAuditLogsPage";
import { LlmExtractionsPage } from "./pages/LlmExtractionsPage";
import { ReceiptLlmExtractionsPage } from "./pages/ReceiptLlmExtractionsPage";
import { RecurringInvoiceSchedulesPage } from "./pages/RecurringInvoiceSchedulesPage";
import { RecurringInvoiceSchedulePage } from "./pages/RecurringInvoiceSchedulePage";
import { ThemeProvider } from "./components/ThemeProvider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Header } from "./components/Header";
import { AdminSidebar } from "./components/AdminSidebar";
import { RecordNavProvider } from "./components/RecordNav";
import { AuthProvider } from "./components/AuthProvider";
import { RequireAuth } from "./components/RequireAuth";
import "./index.css";

interface EntityJumpInputProps {
  label: string;
  placeholder?: string;
  basePath: string;
}

function EntityJumpInput({ label, placeholder, basePath }: EntityJumpInputProps) {
  const [value, setValue] = useState("");
  const navigate = useNavigate();

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const id = value.trim();
    if (!id) return;
    navigate(`${basePath}/${encodeURIComponent(id)}`);
  }

  return (
    <form onSubmit={handleSubmit} className="group flex items-center gap-3 px-4 py-2 focus-within:bg-accent/50">
      <label htmlFor={`jump-${basePath}`} className="w-28 shrink-0 text-sm text-muted-foreground">
        {label}
      </label>
      <Input
        id={`jump-${basePath}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        className="h-8 border-transparent bg-transparent font-mono text-xs shadow-none focus-visible:border-input dark:bg-transparent"
      />
      <button
        type="submit"
        aria-label={`Go to ${label}`}
        className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-focus-within:opacity-100"
      >
        <CornerDownLeftIcon className="size-4" />
      </button>
    </form>
  );
}

const ENTITY_JUMPS: EntityJumpInputProps[] = [
  { label: "Client", placeholder: "client ID", basePath: "/clients" },
  { label: "Organisation", placeholder: "organisation ID", basePath: "/organisations" },
  { label: "User", placeholder: "user ID", basePath: "/users" },
  { label: "Invoice", placeholder: "invoice ID", basePath: "/invoices" },
  { label: "Recurring inv.", placeholder: "schedule ID", basePath: "/recurring-invoices" },
  { label: "Customer", placeholder: "customer ID", basePath: "/customers" },
  { label: "Product", placeholder: "product ID", basePath: "/products" },
  { label: "Bill", placeholder: "bill ID", basePath: "/bills" },
  { label: "Expense", placeholder: "expense ID", basePath: "/expenses" },
  { label: "Match group", placeholder: "match group ID", basePath: "/match-groups" },
  { label: "Supplier", placeholder: "supplier ID", basePath: "/suppliers" },
  { label: "Gmail conn.", placeholder: "Gmail connection ID", basePath: "/gmail-connections" },
  { label: "Agent conv.", placeholder: "conversation ID", basePath: "/agent/conversations" },
];

function Home() {
  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-10">
      <h1 className="text-xl font-semibold tracking-tight">Jump to a record</h1>
      <p className="mt-1 text-sm text-muted-foreground">Paste an ID and press Enter. ⌘K searches pages.</p>
      <div className="mt-6 divide-y overflow-hidden rounded-lg border bg-card">
        {ENTITY_JUMPS.map((entity) => (
          <EntityJumpInput key={entity.basePath} {...entity} />
        ))}
      </div>
    </div>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <TooltipProvider>
        <AuthProvider>
          <BrowserRouter>
            <RequireAuth>
              <RecordNavProvider>
              <div className="flex h-screen overflow-hidden">
                <AdminSidebar className="hidden md:flex" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <Header />
                  <main className="flex-1 overflow-auto">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/three-way-match" element={<ThreeWayMatchPage />} />
                <Route path="/user-activity" element={<UserActivityPage />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/clients/:clientId" element={<ClientPage />} />
                <Route path="/clients/:clientId/organisations" element={<ClientOrganisationsPage />} />
                <Route path="/organisations" element={<OrganisationsListPage />} />
                <Route path="/organisations/:organisationId" element={<OrganisationPage />} />
                <Route path="/companies/:companyId" element={<CompanyPage />} />
                <Route path="/company-inbox-bills/:billId" element={<CompanyInboxBillPage />} />
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/invoices/:invoiceId" element={<InvoicePage />} />
                <Route path="/invoice-reminders" element={<InvoiceRemindersPage />} />
                <Route path="/recurring-invoices" element={<RecurringInvoiceSchedulesPage />} />
                <Route path="/recurring-invoices/:scheduleId" element={<RecurringInvoiceSchedulePage />} />
                <Route path="/invoice-audit-logs" element={<InvoiceAuditLogsPage />} />
                <Route path="/bill-audit-logs" element={<BillAuditLogsPage />} />
                <Route path="/modules-audit-logs" element={<ModuleAuditLogsPage />} />
                <Route path="/llm-extractions" element={<LlmExtractionsPage />} />
                <Route path="/receipt-llm-extractions" element={<ReceiptLlmExtractionsPage />} />
                <Route path="/webhook-deliveries" element={<WebhookDeliveriesPage />} />
                <Route path="/received-e-invoices" element={<ReceivedEInvoicesPage />} />
                <Route path="/sent-e-invoices" element={<SentEInvoicesPage />} />
                <Route
                  path="/e-invoicing-registrations"
                  element={<EInvoicingRegistrationsPage />}
                />
                <Route path="/customers/:customerId" element={<CustomerPage />} />
                <Route path="/products/:productId" element={<ProductPage />} />
                <Route path="/bills" element={<BillsPage />} />
                <Route path="/bills/:billId" element={<BillPage />} />
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route path="/expenses/:expenseId" element={<ExpensePage />} />
                <Route path="/match-groups" element={<MatchGroupsPage />} />
                <Route path="/match-groups/:matchGroupId" element={<MatchGroupPage />} />
                <Route path="/purchase-orders/:purchaseOrderId" element={<PurchaseOrderPage />} />
                <Route path="/delivery-notes/:deliveryNoteId" element={<DeliveryNotePage />} />
                <Route path="/suppliers/:supplierId" element={<SupplierPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/demos" element={<DemosPage />} />
                <Route path="/users/:userId" element={<UserPage />} />
                <Route path="/countries" element={<CountriesPage />} />
                <Route path="/currencies" element={<CurrenciesPage />} />
                <Route path="/industries" element={<IndustriesPage />} />
                <Route path="/country-tax-rates" element={<CountryTaxRatesPage />} />
                <Route path="/agent/conversations/:conversationId" element={<AgentConversationPage />} />
                <Route path="/gmail-connections" element={<GmailConnectionsPage />} />
                <Route path="/gmail-connections/:gmailConnectionId" element={<GmailConnectionPage />} />
              </Routes>
                  </main>
                </div>
              </div>
              </RecordNavProvider>
            </RequireAuth>
            <Toaster />
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
